import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import {visuallyHidden} from '@mui/utils';
import {StyledTableSortLabel} from './StyledTableSortLabel.ts';
import {DetectorResendSortedTableFields, getComparator} from '../../utils/tableUtils.ts'; // adjust path accordingly

interface DetectorData {
  name: string;
  status: string;
  tags: number[];
}

export interface DetectorResendData {
  detectors: DetectorData[];
  tag_Id: string;
  from_date: string;
  toDate: string;
  detections: number;
  resends: number;
  avg_rssi: number;
  avg_resend_counter: number;
  max_resend_counter: number;
  min_resend_counter: number;
  resends_per_hour?: number;
  detections_per_hour?: number;
  problem_score?: number;
}

const getScoreCategory = (
  score: number,
): {label: string; color: 'error' | 'warning' | 'success'} => {
  if (score >= 80) return {label: 'High Risk', color: 'error'};
  if (score >= 50) return {label: 'Moderate', color: 'warning'};
  return {label: 'Safe', color: 'success'};
};

interface Props {
  data: DetectorResendData[];
  isLoading: boolean;
  error?: Error;
}

const MaterialUITable: React.FC<Props> = ({data, isLoading, error}) => {
  // const { data = [], isLoading, error } = useFetchEventTagResendStat();
  const [orderBy, setOrderBy] = useState<string>('fromDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [visibleRows, setVisibleRows] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('High Risk');

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!showAll || !loadMoreRef.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleRows((prev) => Math.min(prev + 10, data.length));
      }
    });
    observer.current.observe(loadMoreRef.current);
  }, [showAll, visibleRows, data.length]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredData = useMemo(() => {
    let _data: any[] = data ? data : [];
    _data = _data.map((row: any) => {
      return JSON.parse(row);
    });
    return _data.filter(
      (row) => {
        const category = getScoreCategory(row.problem_score ?? 0).label;
        const matchesCategory = categoryFilter === 'All' || category === categoryFilter;
        const matchesSearch =
          Object.values(row).some((value) =>
            String(value).toLowerCase().includes(search),
          ) || row.detectors.some((d) => d.name.toLowerCase().includes(search));
        return matchesCategory && matchesSearch;
      },
      [data, search, categoryFilter],
    );
  });

  const dataWithScore = filteredData.map((row) => {
    const category = getScoreCategory(row.problem_score ?? 0);
    return {
      ...row,
      category: category,
    };
  });

  const sortedData = React.useMemo(
    () => [...dataWithScore].sort(getComparator<DetectorResendData>(order, orderBy)),
    [order, dataWithScore, orderBy],
  );

  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    handleRequestSort(event, property);
  };

  const visibleData = showAll
    ? sortedData.slice(0, visibleRows)
    : sortedData.slice(0, 10);
  return (
    <Paper style={{width: '100%', maxHeight: '100vh', overflowY: 'auto'}}>
      <Box display="flex" gap={1} p={2} alignItems="center">
        <Box flex={5}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
          />
        </Box>
        <Box flex={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="High Risk">High Risk</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Safe">Safe</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box flex={2}>
          <Button fullWidth variant="outlined" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Top 10' : 'Show All'}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Box color="error.main" p={4}>
          Error: {error.message}
        </Box>
      ) : (
        <TableContainer sx={{height: '80vh'}}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tag ID</TableCell>
                <TableCell>
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.fromDate.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.fromDate.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.fromDate.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.fromDate.text}
                    {orderBy === DetectorResendSortedTableFields.fromDate.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell>To Date</TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.detections.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.detections.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.detections.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.detections.text}
                    {orderBy === DetectorResendSortedTableFields.detections.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.resends.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.resends.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.resends.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.resends.text}
                    {orderBy === DetectorResendSortedTableFields.resends.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.avgRssi.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.avgRssi.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.avgRssi.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.avgRssi.text}
                    {orderBy === DetectorResendSortedTableFields.avgRssi.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.avgResend.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.avgResend.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.avgResend.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.avgResend.text}
                    {orderBy === DetectorResendSortedTableFields.avgResend.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.maxResend.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.maxResend.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.maxResend.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.maxResend.text}
                    {orderBy === DetectorResendSortedTableFields.maxResend.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell align="right">Min Resend</TableCell>
                <TableCell align="right">
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.score.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.score.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(DetectorResendSortedTableFields.score.key)}
                  >
                    {DetectorResendSortedTableFields.score.text}
                    {orderBy === DetectorResendSortedTableFields.score.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
                <TableCell>
                  <StyledTableSortLabel
                    active={orderBy === DetectorResendSortedTableFields.category.key}
                    direction={
                      orderBy === DetectorResendSortedTableFields.category.key
                        ? order
                        : 'asc'
                    }
                    onClick={createSortHandler(
                      DetectorResendSortedTableFields.category.key,
                    )}
                  >
                    {DetectorResendSortedTableFields.category.text}
                    {orderBy === DetectorResendSortedTableFields.category.key ? (
                      <Box component="span" sx={visuallyHidden}>
                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </StyledTableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {visibleData.map((row, idx) => {
                return (
                  <TableRow key={idx}>
                    <TableCell>{row.detectors[0]?.name || 'N/A'}</TableCell>
                    <TableCell>{row.tag_id}</TableCell>
                    <TableCell>{new Date(row.from_date).toDateString()}</TableCell>
                    <TableCell>{new Date(row.to_date).toDateString()}</TableCell>
                    {/*<TableCell>{format(new Date(row.fromDate), 'PPpp')}</TableCell>*/}
                    {/*<TableCell>{format(new Date(row.toDate), 'PPpp')}</TableCell>*/}
                    <TableCell align="right">{row.detections}</TableCell>
                    <TableCell align="right">{row.resends}</TableCell>
                    <TableCell align="right">{row.avg_rssi}</TableCell>
                    <TableCell align="right">{row.avg_resend_counter}%</TableCell>
                    <TableCell align="right">{row.max_resend_counter}</TableCell>
                    <TableCell align="right">{row.min_resend_counter}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Score = 0.6*Resends/hr + 0.5*AvgResend + 0.3*RSSI - 0.4*Detections/hr">
                        <span>{row.problem_score?.toFixed(2)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.category.label}
                        color={row.category.color}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
              {showAll && visibleRows < sortedData.length && (
                <TableRow ref={loadMoreRef}>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default MaterialUITable;
